import React, {
  useState, useCallback, memo
} from "react";
import {
  Box, IconButton, Menu, MenuItem,
  Typography, Chip, Tooltip,
  Divider, CircularProgress, Fade,
  Tab, Tabs
} from "@mui/material";
import LanguageIcon
  from "@mui/icons-material/Language";
import CheckIcon
  from "@mui/icons-material/Check";
import TranslateIcon
  from "@mui/icons-material/Translate";
import { useLanguage }
  from "../context/LanguageContext";
import { LANG_GROUPS }
  from "../i18n/translations";

const LanguageDropdown = memo(
  function LanguageDropdown({
    compact = false,
    dark = false
  }) {
    const {
      language,
      changeLanguage,
      langMeta,
      isMixed,
      availableLanguages
    } = useLanguage();

    const [anchor, setAnchor] = useState(null);
    const [tab, setTab] = useState(
      isMixed ? 1 : 0
    );
    const [saving, setSaving] = useState(false);
    const [savingCode, setSavingCode] =
      useState(null);

    const open = Boolean(anchor);

    const handleOpen = (e) => {
      setAnchor(e.currentTarget);
      // Open on correct tab for current lang
      setTab(isMixed ? 1 : 0);
    };

    const handleClose = () => {
      setAnchor(null);
    };

    const handleSelect = useCallback(
      async (code) => {
        if (code === language) {
          handleClose();
          return;
        }
        setSaving(true);
        setSavingCode(code);
        try {
          await changeLanguage(code);
        } finally {
          setSaving(false);
          setSavingCode(null);
          handleClose();
        }
      },
      [language, changeLanguage]
    );

    // Get lang object
    const getLang = (code) =>
      availableLanguages.find(
        l => l.code === code
      );

    // Get codes for current tab
    const currentGroupCodes = tab === 0
      ? LANG_GROUPS.pure.codes
      : LANG_GROUPS.mixed.codes;

    const currentLangs = currentGroupCodes
      .map(code => getLang(code))
      .filter(Boolean);

    // Group pure langs by region
    const REGION_GROUPS = [
      {
        label: "North India",
        codes: ["hi","pa","ur","ks"]
      },
      {
        label: "East India",
        codes: ["bn","as","or","mni","ne"]
      },
      {
        label: "West India",
        codes: ["mr","gu"]
      },
      {
        label: "South India",
        codes: ["ta","te","kn","ml"]
      },
      {
        label: "International",
        codes: ["en"]
      }
    ];

    return (
      <>
        {/* ── Trigger Button ────────────────── */}
        <Tooltip title="Language / भाषा">
          <IconButton
            onClick={handleOpen}
            size="small"
            sx={{
              border: "1px solid",
              borderColor: dark
                ? "rgba(255,255,255,0.4)"
                : "rgba(46,125,50,0.35)",
              borderRadius: 2,
              px: compact ? 0.5 : 1.2,
              py: 0.5,
              color: dark ? "white" : "#2E7D32",
              gap: 0.5,
              "&:hover": {
                bgcolor: dark
                  ? "rgba(255,255,255,0.15)"
                  : "rgba(46,125,50,0.08)",
                borderColor: dark
                  ? "white" : "#2E7D32"
              },
              transition: "all 0.2s"
            }}
          >
            {isMixed
              ? <TranslateIcon
                  sx={{ fontSize: 18 }}
                />
              : <LanguageIcon
                  sx={{ fontSize: 18 }}
                />
            }
            {!compact && (
              <Box sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                lineHeight: 1
              }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    fontSize: "0.72rem",
                    lineHeight: 1.2
                  }}
                >
                  {langMeta?.flag}{" "}
                  {langMeta?.shortName ||
                    langMeta?.nativeName}
                </Typography>
                {isMixed && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.55rem",
                      opacity: 0.8,
                      lineHeight: 1
                    }}
                  >
                    +English
                  </Typography>
                )}
              </Box>
            )}
            {compact && (
              <Typography
                variant="caption"
                sx={{ fontSize: "0.7rem" }}
              >
                {langMeta?.flag}
              </Typography>
            )}
          </IconButton>
        </Tooltip>

        {/* ── Dropdown Menu ─────────────────── */}
        <Menu
          anchorEl={anchor}
          open={open}
          onClose={handleClose}
          TransitionComponent={Fade}
          PaperProps={{
            sx: {
              width: 280,
              maxHeight: 520,
              borderRadius: 2,
              boxShadow:
                "0 8px 32px rgba(0,0,0,0.15)",
              border: "1px solid #E8F5E9",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column"
            }
          }}
          MenuListProps={{
            sx: {
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflow: "hidden",
              p: 0
            }
          }}
          transformOrigin={{
            horizontal: "right",
            vertical: "top"
          }}
          anchorOrigin={{
            horizontal: "right",
            vertical: "bottom"
          }}
        >
          {/* Header */}
          <Box sx={{
            px: 2, py: 1.5,
            bgcolor: "#2E7D32",
            color: "white",
            flexShrink: 0
          }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
            >
              🌐 Select Language
            </Typography>
            <Typography
              variant="caption"
              sx={{ opacity: 0.85 }}
            >
              भाषा चुनें · ভাষা বেছে নিন
            </Typography>
          </Box>

          {/* Pure / Mixed tabs */}
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="fullWidth"
            textColor="inherit"
            TabIndicatorProps={{
              style: { background: "#2E7D32" }
            }}
            sx={{
              minHeight: 36,
              bgcolor: "#F5F5F5",
              flexShrink: 0,
              "& .MuiTab-root": {
                minHeight: 36,
                fontSize: "0.7rem",
                fontWeight: 600,
                textTransform: "none",
                color: "text.secondary"
              },
              "& .Mui-selected": {
                color: "#2E7D32 !important"
              }
            }}
          >
            <Tab
              label="Pure Language"
              icon={<LanguageIcon
                sx={{ fontSize: 14 }}
              />}
              iconPosition="start"
            />
            <Tab
              label="+ English Mix"
              icon={<TranslateIcon
                sx={{ fontSize: 14 }}
              />}
              iconPosition="start"
            />
          </Tabs>

          {/* Tab description */}
          <Box sx={{
            px: 2, py: 0.8,
            bgcolor: tab === 0
              ? "#E8F5E9" : "#FFF3E0",
            flexShrink: 0
          }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.62rem" }}
            >
              {tab === 0
                ? "✅ Only that language. " +
                  "No English words in AI replies."
                : "🔀 Natural mix of local " +
                  "language + English. " +
                  "Like how young farmers talk."
              }
            </Typography>
          </Box>

          {/* Language list */}
          <Box sx={{ overflow: "auto", flex: 1 }}>
            {tab === 0 ? (
              // Pure — grouped by region
              REGION_GROUPS.map((group, gi) => {
                const groupLangs = group.codes
                  .map(code => getLang(code))
                  .filter(Boolean);
                if (!groupLangs.length)
                  return null;

                return (
                  <Box key={gi}>
                    <Typography
                      variant="caption"
                      sx={{
                        px: 2,
                        pt: gi === 0 ? 1 : 0.5,
                        pb: 0.3,
                        display: "block",
                        color: "text.disabled",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontSize: "0.58rem"
                      }}
                    >
                      {group.label}
                    </Typography>

                    {groupLangs.map((lang) =>
                      renderLangItem(
                        lang,
                        language,
                        saving,
                        savingCode,
                        handleSelect
                      )
                    )}

                    {gi < REGION_GROUPS.length - 1 && (
                      <Divider
                        sx={{ my: 0.5 }}
                      />
                    )}
                  </Box>
                );
              })
            ) : (
              // Mixed — flat list with example
              <Box sx={{ pt: 0.5 }}>
                {currentLangs.map((lang) =>
                  renderMixedItem(
                    lang,
                    language,
                    saving,
                    savingCode,
                    handleSelect
                  )
                )}
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box sx={{
            px: 2, py: 0.8,
            borderTop: "1px solid #E0E0E0",
            bgcolor: "#FAFAFA",
            flexShrink: 0
          }}>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontSize: "0.58rem" }}
            >
              Saved to profile automatically
            </Typography>
          </Box>
        </Menu>
      </>
    );
  }
);

// ─── Pure language list item ───────────────────────
function renderLangItem(
  lang, current, saving,
  savingCode, onSelect
) {
  const isSelected = current === lang.code;
  const isSaving   = saving &&
    savingCode === lang.code;

  return (
    <MenuItem
      key={lang.code}
      onClick={() => onSelect(lang.code)}
      selected={isSelected}
      dense
      sx={{
        py: 0.7, px: 2,
        mx: 0.5, mb: 0.2,
        borderRadius: 1,
        bgcolor: isSelected
          ? "#E8F5E9 !important"
          : "transparent",
        "&:hover": { bgcolor: "#F1F8E9" }
      }}
    >
      <Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 1, flex: 1
      }}>
        <Typography sx={{ fontSize: "1rem" }}>
          {lang.flag}
        </Typography>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            fontWeight={isSelected ? 700 : 500}
            color={
              isSelected
                ? "success.dark"
                : "text.primary"
            }
            sx={{
              lineHeight: 1.3,
              fontFamily:
                "var(--app-font," +
                "'Roboto',sans-serif)"
            }}
          >
            {lang.nativeName}
          </Typography>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ fontSize: "0.62rem" }}
          >
            {lang.name}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ width: 18 }}>
        {isSaving ? (
          <CircularProgress
            size={12} color="success"
          />
        ) : isSelected ? (
          <CheckIcon
            sx={{ fontSize: 14, color:"#2E7D32" }}
          />
        ) : null}
      </Box>
    </MenuItem>
  );
}

// ─── Mixed language list item ──────────────────────
function renderMixedItem(
  lang, current, saving,
  savingCode, onSelect
) {
  const isSelected = current === lang.code;
  const isSaving   = saving &&
    savingCode === lang.code;

  return (
    <Box
      key={lang.code}
      onClick={() => onSelect(lang.code)}
      sx={{
        px: 2, py: 0.8,
        mx: 0.5, mb: 0.3,
        borderRadius: 1.5,
        cursor: "pointer",
        bgcolor: isSelected
          ? "#FFF3E0" : "transparent",
        border: isSelected
          ? "1px solid #FFB74D"
          : "1px solid transparent",
        "&:hover": { bgcolor: "#FFF8E1" },
        transition: "all 0.15s"
      }}
    >
      <Box sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1
      }}>
        <Typography sx={{
          fontSize: "1rem", mt: 0.2
        }}>
          {lang.flag}
        </Typography>
        <Box sx={{ flex: 1 }}>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5
          }}>
            <Typography
              variant="body2"
              fontWeight={isSelected ? 700 : 600}
              color={
                isSelected
                  ? "#E65100"
                  : "text.primary"
              }
            >
              {lang.nativeName}
            </Typography>
            <Chip
              label="MIX"
              size="small"
              sx={{
                height: 14,
                fontSize: "0.5rem",
                bgcolor: "#FFF3E0",
                color: "#E65100",
                fontWeight: 700,
                "& .MuiChip-label": { px: 0.6 }
              }}
            />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.62rem" }}
          >
            {lang.mixLabel}
          </Typography>
          {/* Example sentence */}
          {lang.example && isSelected && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.3,
                fontSize: "0.6rem",
                color: "#E65100",
                fontStyle: "italic"
              }}
            >
              e.g. "{lang.example}"
            </Typography>
          )}
        </Box>
        <Box sx={{ width: 18, flexShrink: 0 }}>
          {isSaving ? (
            <CircularProgress
              size={12}
              sx={{ color: "#E65100" }}
            />
          ) : isSelected ? (
            <CheckIcon sx={{
              fontSize: 14,
              color: "#E65100"
            }} />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

export default LanguageDropdown;
